import React, { useCallback, useRef, useMemo, useContext, startTransition } from 'react';
import throttle from 'lodash/throttle';
import { useTranslation } from 'react-i18next';
import { URLS } from '@wix/da-url';
import { useOnOutsideClick } from '@wix/da-hooks/pkg/useOnOutsideClick';
import ChatIcon from '@wix/da-ds/pkg/Icons/24x24/Chat';
import { BiData } from '@wix/da-bi/pkg/events';
import IconButtonWithNotification from '@wix/da-ds/pkg/Buttons/IconButtonWithNotification';
import { useDropdownHover } from '../../hooks/useDropdownHover';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';
import { DamzContext } from '@wix/da-react-context/pkg/DamzContext';
import cn from 'classnames';
import s from './DamzDropdownWrapper.scss';
const DamzDropdownWrapper = ({ unreadCount, className, iconClassName, numberClassName, openDamzDropdown, closeDamzDropdown, isDamzPageOpen, isDamzDropdownOpen, toggleDamzPage, toggleBurgerMenu, 'aria-label': ariaLabel })=>{
    const { t } = useTranslation();
    const { DrawerComponent, isStandalonePage } = useContext(DamzContext);
    const { handleDropdownHover, handleDropdownLeave } = useDropdownHover({
        isDropdownOpen: isDamzDropdownOpen,
        openDropdown: openDamzDropdown,
        closeDropdown: closeDamzDropdown
    });
    // no point to spamming this if our guest has twitchy hands
    const throttledMouseLeave = useMemo(()=>throttle(handleDropdownLeave, 300), [
        handleDropdownLeave
    ]);
    const rootRef = useRef(null);
    const onClose = useCallback(()=>{
        throttledMouseLeave();
    }, [
        throttledMouseLeave
    ]);
    const handleClickOutside = useCallback(()=>{
        if (!isDamzDropdownOpen) {
            return;
        }
        onClose();
    }, [
        isDamzDropdownOpen,
        onClose
    ]);
    useOnOutsideClick(rootRef, handleClickOutside);
    const handleIconClick = useCallback((e)=>{
        if (!isStandalonePage) {
            // We can use startTransition outright. useTransition's pending state is unnecessary since
            // a loading screen gets mounted immediately.
            startTransition(()=>{
                yieldToMain(()=>{
                    toggleBurgerMenu(false); // Close network bar, if it's open
                    toggleDamzPage();
                });
            });
        }
        // We put an `href` attribute on the element that calls this so users can open notifications in a new tab,
        // so we prevent default to avoid the standard handling of an anchor tag click since we're duper browsing.
        e.preventDefault();
    }, [
        isStandalonePage,
        toggleDamzPage,
        toggleBurgerMenu
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: className,
        onMouseEnter: ()=>{
            if (!isDamzPageOpen && !isStandalonePage) {
                handleDropdownHover();
            }
        },
        onMouseLeave: ()=>handleDropdownLeave(),
        ref: rootRef
    }, /*#__PURE__*/ React.createElement(IconButtonWithNotification, {
        className: s['above-dropdown'],
        icon: ChatIcon,
        size: "medium",
        notificationColor: "red",
        iconButtonClassName: iconClassName,
        numberClassName: numberClassName,
        count: unreadCount,
        biData: BiData({
            evid: 700,
            link_name: 'damz',
            indicator: unreadCount
        }),
        href: isStandalonePage || isDamzPageOpen ? undefined : URLS.CHAT_HOME,
        onClick: handleIconClick,
        "aria-label": t('siteHeader.a11y.cart.withItems', {
            formattedCount: unreadCount
        })
    }), DrawerComponent && !isStandalonePage && /*#__PURE__*/ React.createElement("div", {
        role: "dialog",
        id: "site-header-damz",
        className: s['wrapper']
    }, isDamzDropdownOpen && /*#__PURE__*/ React.createElement("div", {
        className: cn(s['container'], s['active'])
    }, /*#__PURE__*/ React.createElement("div", {
        className: "ds-surface-tertiary"
    }, /*#__PURE__*/ React.createElement(DrawerComponent, {
        isDropdown: true,
        openDamzPage: ()=>toggleDamzPage && toggleDamzPage()
    })))));
};
DamzDropdownWrapper.displayName = 'DamzDropdownWrapper';
export default DamzDropdownWrapper;

//# sourceMappingURL=DamzDropdownWrapper.js.map